import * as React from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import SkeletonTableRows from './SkeletonTableRows';
import DoneIcon from '@mui/icons-material/Done';
import BlockIcon from '@mui/icons-material/Block';
import { visuallyHidden } from '@mui/utils';
import Button from '@mui/material/Button';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAcceptUserMutation, useGetUserRequestsQuery, useRejectUserMutation, useGetClubBillingQuery, useGetClubMembersQuery } from '../api/apiSlice';
import { AdminRequest } from '../types/api';
import { Order, getComparator, stableSort } from '../utils/tableSort';
import { formatShortDate } from '../utils/formatDate';

const headCellsBase: { id: keyof AdminRequest; numeric: boolean; disablePadding: boolean; labelKey: string }[] = [
  {
    id: 'NAME',
    numeric: false,
    disablePadding: true,
    labelKey: 'userManagement.name',
  },
  {
    id: 'FIRSTNAME',
    numeric: false,
    disablePadding: false,
    labelKey: 'userManagement.firstName',
  },
  {
    id: 'EMAIL',
    numeric: false,
    disablePadding: false,
    labelKey: 'userManagement.email',
  },
  {
    id: 'DATE',
    numeric: false,
    disablePadding: false,
    labelKey: 'userRequests.requestDate',
  },
];

interface EnhancedTableHeadProps {
  order: Order;
  orderBy: string;
  rowCount: number;
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof AdminRequest) => void;
}

function EnhancedTableHead({ order, orderBy, onRequestSort }: EnhancedTableHeadProps) {
  const { t } = useTranslation();
  const createSortHandler = (property: keyof AdminRequest) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>

        {headCellsBase.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={'center'}
            padding={headCell.disablePadding ? 'normal' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {t(headCell.labelKey as 'userManagement.name')}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? t('common.sortedDescending') : t('common.sortedAscending')}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

interface UserRequestProps {
  admin?: number;
  clubId?: number;
}

export default function UserRequest({ admin, clubId }: UserRequestProps) {
  const { t, i18n } = useTranslation();
  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] = React.useState<keyof AdminRequest>('NAME');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [errorSnackbarOpen, setErrorSnackbarOpen] = React.useState(false);
  const clubID = clubId != null ? String(clubId) : '';

  const navigate = useNavigate();

  const { data: requests = [], isLoading } = useGetUserRequestsQuery(clubID, { skip: !clubID });
  const [acceptUser] = useAcceptUserMutation();
  const [rejectUser] = useRejectUserMutation();

  // Soft nudge only -- the starter plan's 30-member cap is deliberately not
  // hard-enforced; accepting still works, this just points the admin at the
  // upgrade path in ClubSettings.
  const { data: billing } = useGetClubBillingQuery(clubId as number, { skip: clubId == null });
  const { data: members = [] } = useGetClubMembersQuery(clubID, { skip: !clubID });
  const STARTER_MEMBER_LIMIT = 30;
  const overStarterLimit =
    (billing?.PLAN ?? 'starter') === 'starter' && members.length >= STARTER_MEMBER_LIMIT;

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof AdminRequest) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAcceptUser = async (user_id: number, admin_id?: number) => {
    if (admin_id === undefined) return;
    try {
      await acceptUser({ userID: user_id, adminID: admin_id }).unwrap();
    } catch (error) {
      console.error('Error:', error);
      setErrorSnackbarOpen(true);
    }
  }

  const handleRejectUser = async (user_id: number) => {
    try {
      await rejectUser(user_id).unwrap();
    } catch (error) {
      console.error('Error:', error);
      setErrorSnackbarOpen(true);
    }
  }

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - requests.length) : 0;

  const visibleRows = React.useMemo(
    () =>
      stableSort(requests, getComparator<AdminRequest, keyof AdminRequest>(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      ),
    [order, orderBy, page, rowsPerPage, requests],
  );

  return (
    <Box sx={{ width: '100%' }}>
      {overStarterLimit && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate('/administration', { state: { selectedTab: 4 } })}
            >
              {t('userRequests.upgradeCta')}
            </Button>
          }
        >
          {t('userRequests.starterLimitWarning', { limit: STARTER_MEMBER_LIMIT })}
        </Alert>
      )}
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer sx={{ overflowX: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table
            sx={{ minWidth: { xs: 450, sm: 750 } }}
            aria-labelledby="tableTitle"
            size={'medium'}
          >
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={requests.length}
            />
            <TableBody>
              {isLoading && (
                <SkeletonTableRows
                  rows={3}
                  cells={[
                    {}, {}, {}, {},
                    { variant: 'circular', width: 24, height: 24, center: true },
                    { variant: 'circular', width: 24, height: 24, center: true },
                  ]}
                />
              )}

              {!isLoading && requests.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {t('userRequests.empty')}
                  </TableCell>
                </TableRow>
              )}

              {visibleRows.map((row, index) => {
              const labelId = `enhanced-table-checkbox-${index}`;

              return (
                <TableRow
                  key={row.ID}
                  hover
                  tabIndex={-1}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell component="th" id={labelId} scope="row" padding="normal" align='center'>
                    {row.NAME}
                  </TableCell>
                  <TableCell align="center">{row.FIRSTNAME}</TableCell>
                  <TableCell align="center">{row.EMAIL}</TableCell>
                  <TableCell align="center">
                    {formatShortDate(row.DATE, i18n.language)}
                  </TableCell>

                  <TableCell align='center' component="th" scope="row">
                    <IconButton onClick={() => handleAcceptUser(row.ID, admin)} aria-label={`${t('common.approve')}: ${row.NAME}`}>
                      <DoneIcon color='success'/>
                    </IconButton>
                  </TableCell>

                  <TableCell align='center' component="th" scope="row">
                    <IconButton onClick={() => handleRejectUser(row.ID)} aria-label={`${t('common.reject')}: ${row.NAME}`}>
                      <BlockIcon color='error' />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: (53) * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={requests.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      <Snackbar open={errorSnackbarOpen} autoHideDuration={4000} onClose={() => setErrorSnackbarOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setErrorSnackbarOpen(false)} severity="error" variant="filled">
          {t('common.genericError')}
        </Alert>
      </Snackbar>
    </Box>
  );
}
